// Botón con variantes basadas en class-variance-authority.
// Consume tokens del sistema de diseño (apps/web/src/styles/tokens.css).
//
// Variantes:
//   primary  → accent (rojo marca) sobre accent-ink
//   secondary → surface-elevated + border (mismo nivel que primary pero outline)
//   ghost    → transparente, hover surface-overlay
//   outline  → border-border + bg transparente
//   danger   → crit (rojo error) sobre ink
//
// Tamaños: sm (h-8), md (h-10, default), lg (h-12), touch (h-11 / min-w-11 = 44 px,
// objetivo táctil mínimo para las pantallas del líder en tableta)
// Loading: muestra spinner inline + deshabilita el botón

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { Spinner } from './spinner';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'whitespace-nowrap',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-ink hover:bg-accent-hover',
        secondary: 'bg-surface-elevated text-fg border border-border hover:bg-surface-overlay',
        ghost: 'text-fg-secondary hover:bg-surface-overlay hover:text-fg',
        outline:
          'border border-border bg-transparent text-fg hover:bg-surface-overlay hover:border-border-strong',
        danger: 'bg-crit text-white hover:opacity-90',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        touch: 'h-11 min-w-11 px-4 text-sm',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Si true, deshabilita y muestra spinner inline. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {loading ? <Spinner className="size-3.5" /> : null}
      {children}
    </button>
  );
});
